import clsx from 'clsx';
import PageWrapper from 'components/Common/PageWrapper/PageWrapper';
import { useMergedStyles } from 'hooks/useMergedStyles';
import { cloneElement, FC, ReactElement, useMemo } from 'react';
import { useUserContext } from 'state/contexts/userContext/userContext';
import { SeoData } from 'types/common';
import { Styles } from 'types/styles';
import LoaderCircle from 'components/Common/LoaderCircle/LoaderCircle';
import RedirectIfNotStorageProvider from '../DashboardStorageProviders/RedirectIfNotStorageProvider';
import DashboardPageWrapperNavigation from './DashboardPageWrapperNavigation/DashboardPageWrapperNavigation';

import styles from './DashboardPageWrapper.module.scss';
import DashboardPageWrapperContent from './DashboardPageWrapperContent/DashboardPageWrapperContent';

type Props = {
  seoData: SeoData;
  newStyles?: Styles;
  pageTitleText?: string;
  pageDescriptionText?: string;
  additionalClass: string;
  hasSidebar?: boolean;
  children: ReactElement;
  breadcrumbsItems?: {
    path: string;
    link?: string;
  }[];
};

const DashboardPageWrapper: FC<Props> = ({
  seoData,
  newStyles = {},
  pageTitleText = '',
  pageDescriptionText = '',
  additionalClass,
  hasSidebar = false,
  children,
  breadcrumbsItems = [],
}) => {
  const mergedStyles = useMergedStyles(styles, newStyles);
  const {
    user: { userData },
  } = useUserContext();

  // This is in order to style general components for all dashboard pages content
  // from a single scss file
  const clonedChildren = useMemo(
    () => cloneElement(children, { newstyles: mergedStyles }),
    [mergedStyles, children],
  );

  return (
    <PageWrapper
      seoData={seoData}
      requireAuth
      additionalClass={clsx(mergedStyles['dashboard-page-wrapper'], {
        [additionalClass]: additionalClass,
        [mergedStyles['dashboard-page-has-sidebar']]: hasSidebar,
      })}
      additionalHeaderClass={userData ? mergedStyles['dashboard-header'] : ''}
      additionalFooterClass={userData ? mergedStyles['dashboard-footer'] : ''}
      newStyles={mergedStyles}
      breadcrumbsItems={breadcrumbsItems}
    >
      <RedirectIfNotStorageProvider>
        <DashboardPageWrapperNavigation />

        {userData ? (
          <DashboardPageWrapperContent
            newStyles={mergedStyles}
            pageTitleText={pageTitleText}
            pageDescriptionText={pageDescriptionText}
            breadcrumbsItems={breadcrumbsItems}
          >
            {clonedChildren}
          </DashboardPageWrapperContent>
        ) : (
          <LoaderCircle newStyles={mergedStyles} />
        )}
      </RedirectIfNotStorageProvider>
    </PageWrapper>
  );
};

export default DashboardPageWrapper;
